"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatFriendlyDateTime = exports.nextLocalSlotUtcMs = exports.localDateTimeToUtcMs = exports.formatFriendlyDateTimeFromOffset = exports.formatTimeUntil = exports.formatTime12hFromOffset = exports.formatHoursAndMinutesUntilLocalMidnight = exports.localHHMMToNextUtcMs = exports.localMidnightUtcMsFromOffset = exports.getSecondsUntilLocalMidnightFromOffset = exports.getLocalDateStringFromOffset = void 0;
/**
 * Format a moment in time (UTC ms + offset minutes east of UTC) as 12-hour time, e.g. "7:52 PM".
 * Use for displaying user-local time when server only has UTC.
 */
const pad2 = (n) => String(n).padStart(2, '0');
/** Calendar date YYYY-MM-DD in the user's local offset (minutes east of UTC). */
const getLocalDateStringFromOffset = (offsetMinutes, utcMs = Date.now()) => {
    const localMs = utcMs + offsetMinutes * 60 * 1000;
    const d = new Date(localMs);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    return `${y}-${pad2(m)}-${pad2(day)}`;
};
exports.getLocalDateStringFromOffset = getLocalDateStringFromOffset;
/** Whole seconds from now until the next local midnight for the given offset. */
const getSecondsUntilLocalMidnightFromOffset = (offsetMinutes, utcMs = Date.now()) => {
    const localMs = utcMs + offsetMinutes * 60 * 1000;
    const secOfDay = Math.floor(localMs / 1000) % (24 * 60 * 60);
    const remaining = 24 * 60 * 60 - secOfDay;
    return remaining >= 24 * 60 * 60 ? 0 : remaining;
};
exports.getSecondsUntilLocalMidnightFromOffset = getSecondsUntilLocalMidnightFromOffset;
/** UTC epoch ms for the next local midnight in the given offset. */
const localMidnightUtcMsFromOffset = (offsetMinutes, utcMs = Date.now()) => {
    const remainingSec = (0, exports.getSecondsUntilLocalMidnightFromOffset)(offsetMinutes, utcMs);
    return utcMs + remainingSec * 1000;
};
exports.localMidnightUtcMsFromOffset = localMidnightUtcMsFromOffset;
/**
 * UTC epoch ms for the next occurrence of HH:MM in the player's local offset.
 * If that time today has passed, uses tomorrow.
 */
const localHHMMToNextUtcMs = (hhmm, offsetMinutes, utcMs = Date.now()) => {
    const trimmed = String(hhmm || '').trim();
    if (!/^\d{1,2}:\d{2}$/.test(trimmed)) {
        return null;
    }
    const parts = trimmed.split(':');
    const hour = parseInt(parts[0] ?? '0', 10);
    const minute = parseInt(parts[1] ?? '0', 10) || 0;
    if (!Number.isFinite(hour) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return null;
    }
    const localMs = utcMs + offsetMinutes * 60 * 1000;
    const d = new Date(localMs);
    const y = d.getUTCFullYear();
    const mo = d.getUTCMonth();
    const day = d.getUTCDate();
    let targetFakeLocalMs = Date.UTC(y, mo, day, hour, minute, 0, 0);
    let targetUtcMs = targetFakeLocalMs - offsetMinutes * 60 * 1000;
    if (targetUtcMs <= utcMs) {
        targetFakeLocalMs = Date.UTC(y, mo, day + 1, hour, minute, 0, 0);
        targetUtcMs = targetFakeLocalMs - offsetMinutes * 60 * 1000;
    }
    return targetUtcMs;
};
exports.localHHMMToNextUtcMs = localHHMMToNextUtcMs;
/** Human-readable hours/minutes until local midnight, e.g. "5 hours and 12 minutes". */
const formatHoursAndMinutesUntilLocalMidnight = (offsetMinutes, utcMs = Date.now()) => {
    const secs = (0, exports.getSecondsUntilLocalMidnightFromOffset)(offsetMinutes, utcMs);
    if (secs < 60) {
        return 'less than a minute';
    }
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    if (hrs === 0) {
        return mins === 1 ? '1 minute' : `${mins} minutes`;
    }
    if (mins === 0) {
        return hrs === 1 ? '1 hour' : `${hrs} hours`;
    }
    const hText = hrs === 1 ? '1 hour' : `${hrs} hours`;
    const mText = mins === 1 ? '1 minute' : `${mins} minutes`;
    return `${hText} and ${mText}`;
};
exports.formatHoursAndMinutesUntilLocalMidnight = formatHoursAndMinutesUntilLocalMidnight;
const formatTime12hFromOffset = (utcMs, offsetMinutes) => {
    const userLocalMs = utcMs + offsetMinutes * 60 * 1000;
    const totalMinutes = Math.floor(userLocalMs / 60000);
    const dayMinutes = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    const hour = Math.floor(dayMinutes / 60) % 24;
    const min = dayMinutes % 60;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return h12 + ':' + (min < 10 ? '0' + min : min) + ' ' + ampm;
};
exports.formatTime12hFromOffset = formatTime12hFromOffset;
const formatTimeUntil = (epochSeconds, nowMs = Date.now()) => {
    const start = Number(epochSeconds);
    if (!Number.isFinite(start) || start <= 0)
        return '';
    const diffSeconds = Math.round(start - nowMs / 1000);
    const abs = Math.abs(diffSeconds);
    const minutes = Math.floor(abs / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (diffSeconds > 0) {
        if (diffSeconds < 60)
            return 'Starts in less than a minute';
        if (minutes === 1)
            return 'Starts in 1 minute';
        if (minutes < 60)
            return `Starts in ${minutes} minutes`;
        if (hours === 1)
            return 'Starts in 1 hour';
        if (hours < 24)
            return `Starts in ${hours} hours`;
        if (days === 1)
            return 'Starts in 1 day';
        return `Starts in ${days} days`;
    }
    else {
        if (abs < 60)
            return 'Starting now';
        if (minutes === 1)
            return 'Should have started 1 minute ago';
        if (minutes < 60)
            return `Should have started ${minutes} minutes ago`;
        if (hours === 1)
            return 'Should have started 1 hour ago';
        if (hours < 24)
            return `Should have started ${hours} hours ago`;
        if (days === 1)
            return 'Should have started 1 day ago';
        return `Should have started ${days} days ago`;
    }
};
exports.formatTimeUntil = formatTimeUntil;
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
const localHourFromOffset = (utcMs, offsetMinutes) => {
    const totalMinutes = Math.floor((utcMs + offsetMinutes * 60 * 1000) / 60000);
    const dayMinutes = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    return Math.floor(dayMinutes / 60) % 24;
};
const localWeekdayFromOffset = (utcMs, offsetMinutes) => {
    const localMs = utcMs + offsetMinutes * 60 * 1000;
    return new Date(localMs).getUTCDay();
};
const diffLocalCalendarDays = (targetUtcMs, nowUtcMs, offsetMinutes) => {
    const parseYmd = (value) => {
        const parts = value.split('-').map(Number);
        return Date.UTC(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2] ?? 1);
    };
    const targetDate = parseYmd((0, exports.getLocalDateStringFromOffset)(offsetMinutes, targetUtcMs));
    const todayDate = parseYmd((0, exports.getLocalDateStringFromOffset)(offsetMinutes, nowUtcMs));
    return Math.round((targetDate - todayDate) / 86400000);
};
/**
 * Format epoch seconds as a friendly local date/time string using an explicit UTC offset.
 * Use on the server when displaying times for a specific player timezone.
 */
const formatFriendlyDateTimeFromOffset = (epochSeconds, offsetMinutes, utcMs = Date.now()) => {
    if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) {
        return '';
    }
    const targetUtcMs = epochSeconds * 1000;
    const timeText = (0, exports.formatTime12hFromOffset)(targetUtcMs, offsetMinutes);
    const diffDays = diffLocalCalendarDays(targetUtcMs, utcMs, offsetMinutes);
    if (diffDays === 0) {
        const hour = localHourFromOffset(targetUtcMs, offsetMinutes);
        if (hour >= 18) {
            return `Tonight at ${timeText}`;
        }
        return `Today at ${timeText}`;
    }
    if (diffDays === 1) {
        return `Tomorrow at ${timeText}`;
    }
    if (diffDays === -1) {
        return `Yesterday at ${timeText}`;
    }
    if (diffDays > 1 && diffDays < 7) {
        const dayName = WEEKDAY_SHORT[localWeekdayFromOffset(targetUtcMs, offsetMinutes)] ?? 'Day';
        return `${dayName} at ${timeText}`;
    }
    const localMs = targetUtcMs + offsetMinutes * 60 * 1000;
    const d = new Date(localMs);
    const month = MONTH_SHORT[d.getUTCMonth()] ?? 'Month';
    const day = d.getUTCDate();
    const year = d.getUTCFullYear();
    return `${month} ${day}, ${year} at ${timeText}`;
};
exports.formatFriendlyDateTimeFromOffset = formatFriendlyDateTimeFromOffset;
const localDateTimeToUtcMs = (year, month, day, hour, minute, offsetMinutes) => {
    if (!Number.isFinite(year) ||
        !Number.isFinite(month) ||
        !Number.isFinite(day) ||
        !Number.isFinite(hour) ||
        !Number.isFinite(minute)) {
        return null;
    }
    return Date.UTC(year, month - 1, day, hour, minute, 0, 0) - offsetMinutes * 60 * 1000;
};
exports.localDateTimeToUtcMs = localDateTimeToUtcMs;
const nextLocalSlotUtcMs = (offsetMinutes, utcMs = Date.now(), leadMinutes = 5, slotMinutes = 15) => {
    const slotMs = slotMinutes * 60 * 1000;
    const leadMs = leadMinutes * 60 * 1000;
    const localMs = utcMs + offsetMinutes * 60 * 1000;
    const roundedLocalMs = Math.ceil((localMs + leadMs) / slotMs) * slotMs;
    return roundedLocalMs - offsetMinutes * 60 * 1000;
};
exports.nextLocalSlotUtcMs = nextLocalSlotUtcMs;
const formatFriendlyDateTime = (epochSeconds, now = new Date()) => {
    if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) {
        return '';
    }
    const date = new Date(epochSeconds * 1000);
    const timeText = date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today = startOfDay(now);
    const target = startOfDay(date);
    const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diffDays === 0) {
        const hour = date.getHours();
        if (hour >= 18) {
            return `Tonight at ${timeText}`;
        }
        return `Today at ${timeText}`;
    }
    if (diffDays === 1) {
        return `Tomorrow at ${timeText}`;
    }
    if (diffDays === -1) {
        return `Yesterday at ${timeText}`;
    }
    if (diffDays > 1 && diffDays < 7) {
        const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
        return `${dayName} at ${timeText}`;
    }
    const dateText = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
    return `${dateText} at ${timeText}`;
};
exports.formatFriendlyDateTime = formatFriendlyDateTime;
//# sourceMappingURL=dateTime.js.map